import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  from,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import * as Sentry from '@sentry/react';
import { GRAPHQL_URL, WS_URL } from '~/shared/config';

type TokenGetter = () => Promise<string>;
let tokenGetter: TokenGetter | null = null;

export const setTokenGetter = (getter: TokenGetter) => {
  tokenGetter = getter;
};

const authLink = setContext(
  async (_: unknown, { headers }: { headers?: Record<string, string> }) => {
    if (tokenGetter) {
      try {
        const token = await tokenGetter();
        return {
          headers: {
            ...headers,
            Authorization: `Bearer ${token}`,
          },
        };
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'apollo_auth_token' },
        });
        return { headers };
      }
    }

    return { headers };
  },
);

const httpLink = new HttpLink({
  uri: GRAPHQL_URL,
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: WS_URL,
    connectionParams: async () => {
      if (!tokenGetter) {
        return {};
      }
      try {
        const token = await tokenGetter();
        return { Authorization: `Bearer ${token}` };
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'apollo_ws_auth_token' },
        });
        return {};
      }
    },
  }),
);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  from([authLink, httpLink]),
);

const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache({
    possibleTypes: {
      UserEvent: ['MessageReceived'],
    },
  }),
});

export default client;
